import { createContext, useContext, createSignal, JSX, ParentComponent } from 'solid-js';

export type Language = 'fr' | 'en' | 'es' | 'de' | 'ja' | 'zh';

interface LanguageContextType {
  language: () => Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>();

export const LanguageProvider: ParentComponent = (props) => {
  const [language, setLanguage] = createSignal<Language>('fr');

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language()];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {props.children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Translations
const translations: Record<Language, any> = {
  fr: {
    nav: {
      about: 'À propos',
      experience: 'Expérience',
      projects: 'Projets',
      skills: 'Compétences',
      blog: 'Blog',
      contact: 'Contact'
    },
    hero: {
      title: 'Manager / Architecte d\'Infrastructures Hybrides',
      description: 'Ingénieur passionné par l\'innovation technologique, je suis spécialisé dans la résolution de problématiques complexes et le pilotage de projets stratégiques. Mon leadership collaboratif et mes compétences en communication me permettent de fédérer des équipes pluridisciplinaires autour d\'objectifs communs pour garantir la réussite de chaque projet.',
      location: 'Lille, Hauts-de-France, France'
    },
    experience: {
      title: 'Expérience Professionnelle'
    },
    projects: {
      title: 'Projets Open Source',
      viewOn: 'Voir sur'
    },
    skills: {
      title: 'Compétences',
      certifications: 'Certifications',
      categories: {
        cloud: 'Cloud & Infrastructure',
        data: 'Data & AI',
        devops: 'DevOps & Monitoring',
        programming: 'Programmation',
        management: 'Management'
      }
    },
    blog: {
      title: 'Blog',
      readMore: 'Lire la suite',
      backToBlog: 'Retour au blog',
      loading: 'Chargement...',
      empty: 'Aucun article pour le moment. Revenez bientôt !',
      notFound: 'Article introuvable'
    },
    cv: {
      title: 'Curriculum Vitae',
      download: 'Télécharger le CV',
      loading: 'Chargement...'
    },
    contact: {
      title: 'Contact',
      description: 'Intéressé par une collaboration ou un projet ? N\'hésitez pas à me contacter.'
    },
    footer: {
      rights: 'Tous droits réservés.',
      madeWith: 'Développé avec SolidJS et Bun'
    }
  },
  en: {
    nav: {
      about: 'About',
      experience: 'Experience',
      projects: 'Projects',
      skills: 'Skills',
      blog: 'Blog',
      contact: 'Contact'
    },
    hero: {
      title: 'Manager / Hybrid Infrastructure Architect',
      description: 'I am an engineer passionate about technological innovation, specializing in solving complex problems and leading strategic projects. My collaborative leadership and communication skills enable me to unite multidisciplinary teams around common goals to ensure the success of each project.',
      location: 'Lille, Hauts-de-France, France'
    },
    experience: {
      title: 'Professional Experience'
    },
    projects: {
      title: 'Open Source Projects',
      viewOn: 'View on'
    },
    skills: {
      title: 'Skills',
      certifications: 'Certifications',
      categories: {
        cloud: 'Cloud & Infrastructure',
        data: 'Data & AI',
        devops: 'DevOps & Monitoring',
        programming: 'Programming',
        management: 'Management'
      }
    },
    blog: {
      title: 'Blog',
      readMore: 'Read more',
      backToBlog: 'Back to blog',
      loading: 'Loading...',
      empty: 'No articles yet. Come back soon!',
      notFound: 'Article not found'
    },
    cv: {
      title: 'Curriculum Vitae',
      download: 'Download CV',
      loading: 'Loading...'
    },
    contact: {
      title: 'Contact',
      description: 'Interested in a collaboration or project? Feel free to contact me.'
    },
    footer: {
      rights: 'All rights reserved.',
      madeWith: 'Built with SolidJS and Bun'
    }
  },
  es: {
    nav: {
      about: 'Acerca de',
      experience: 'Experiencia',
      projects: 'Proyectos',
      skills: 'Habilidades',
      blog: 'Blog',
      contact: 'Contacto'
    },
    hero: {
      title: 'Gerente / Arquitecto de Infraestructuras Híbridas',
      description: 'Soy un ingeniero apasionado por la innovación tecnológica, especializado en resolver problemas complejos y liderar proyectos estratégicos. Mi liderazgo colaborativo y habilidades de comunicación me permiten unir equipos multidisciplinarios en torno a objetivos comunes para garantizar el éxito de cada proyecto.',
      location: 'Lille, Altos de Francia, Francia'
    },
    experience: {
      title: 'Experiencia Profesional'
    },
    projects: {
      title: 'Proyectos de Código Abierto',
      viewOn: 'Ver en'
    },
    skills: {
      title: 'Habilidades',
      certifications: 'Certificaciones',
      categories: {
        cloud: 'Nube e Infraestructura',
        data: 'Datos e IA',
        devops: 'DevOps y Monitoreo',
        programming: 'Programación',
        management: 'Gestión'
      }
    },
    blog: {
      title: 'Blog',
      readMore: 'Leer más',
      backToBlog: 'Volver al blog',
      loading: 'Cargando...',
      empty: '¡Aún no hay artículos. Vuelve pronto!',
      notFound: 'Artículo no encontrado'
    },
    cv: {
      title: 'Curriculum Vitae',
      download: 'Descargar CV',
      loading: 'Cargando...'
    },
    contact: {
      title: 'Contacto',
      description: '¿Interesado en una colaboración o proyecto? No dudes en contactarme.'
    },
    footer: {
      rights: 'Todos los derechos reservados.',
      madeWith: 'Desarrollado con SolidJS y Bun'
    }
  },
  de: {
    nav: {
      about: 'Über',
      experience: 'Erfahrung',
      projects: 'Projekte',
      skills: 'Fähigkeiten',
      blog: 'Blog',
      contact: 'Kontakt'
    },
    hero: {
      title: 'Manager / Hybrid-Infrastruktur-Architekt',
      description: 'Ich bin ein Ingenieur, der von technologischer Innovation begeistert ist und sich auf die Lösung komplexer Probleme und die Leitung strategischer Projekte spezialisiert hat. Meine kollaborative Führung und Kommunikationsfähigkeiten ermöglichen es mir, multidisziplinäre Teams um gemeinsame Ziele zu vereinen, um den Erfolg jedes Projekts zu gewährleisten.',
      location: 'Lille, Hauts-de-France, Frankreich'
    },
    experience: {
      title: 'Berufserfahrung'
    },
    projects: {
      title: 'Open-Source-Projekte',
      viewOn: 'Ansehen auf'
    },
    skills: {
      title: 'Fähigkeiten',
      certifications: 'Zertifizierungen',
      categories: {
        cloud: 'Cloud & Infrastruktur',
        data: 'Daten & KI',
        devops: 'DevOps & Überwachung',
        programming: 'Programmierung',
        management: 'Management'
      }
    },
    blog: {
      title: 'Blog',
      readMore: 'Weiterlesen',
      backToBlog: 'Zurück zum Blog',
      loading: 'Wird geladen...',
      empty: 'Noch keine Artikel. Schauen Sie bald wieder vorbei!',
      notFound: 'Artikel nicht gefunden'
    },
    cv: {
      title: 'Lebenslauf',
      download: 'CV herunterladen',
      loading: 'Wird geladen...'
    },
    contact: {
      title: 'Kontakt',
      description: 'Interessiert an einer Zusammenarbeit oder einem Projekt? Kontaktieren Sie mich gerne.'
    },
    footer: {
      rights: 'Alle Rechte vorbehalten.',
      madeWith: 'Entwickelt mit SolidJS und Bun'
    }
  },
  ja: {
    nav: {
      about: '概要',
      experience: '経験',
      projects: 'プロジェクト',
      skills: 'スキル',
      blog: 'ブログ',
      contact: '連絡先'
    },
    hero: {
      title: 'マネージャー / ハイブリッドインフラストラクチャアーキテクト',
      description: '私は技術革新に情熱を持つエンジニアで、複雑な問題の解決と戦略的プロジェクトのリードを専門としています。私の協力的なリーダーシップとコミュニケーションスキルにより、多分野のチームを共通の目標に向けて統合し、各プロジェクトの成功を確実にすることができます。',
      location: 'リール、オー・ド・フランス、フランス'
    },
    experience: {
      title: '職務経験'
    },
    projects: {
      title: 'オープンソースプロジェクト',
      viewOn: '表示'
    },
    skills: {
      title: 'スキル',
      certifications: '認定資格',
      categories: {
        cloud: 'クラウド＆インフラストラクチャ',
        data: 'データ＆AI',
        devops: 'DevOps＆監視',
        programming: 'プログラミング',
        management: 'マネジメント'
      }
    },
    blog: {
      title: 'ブログ',
      readMore: '続きを読む',
      backToBlog: 'ブログに戻る',
      loading: '読み込み中...',
      empty: 'まだ記事がありません。後でまた来てください！',
      notFound: '記事が見つかりません'
    },
    cv: {
      title: '履歴書',
      download: 'CVをダウンロード',
      loading: '読み込み中...'
    },
    contact: {
      title: '連絡先',
      description: 'コラボレーションやプロジェクトに興味がありますか？お気軽にお問い合わせください。'
    },
    footer: {
      rights: '全著作権所有。',
      madeWith: 'SolidJSとBunで構築'
    }
  },
  zh: {
    nav: {
      about: '关于',
      experience: '经验',
      projects: '项目',
      skills: '技能',
      blog: '博客',
      contact: '联系'
    },
    hero: {
      title: '经理 / 混合基础设施架构师',
      description: '我是一名对技术创新充满热情的工程师，专门解决复杂问题和领导战略项目。我的协作领导能力和沟通技巧使我能够将多学科团队团结在共同目标周围，确保每个项目的成功。',
      location: '里尔，上法兰西，法国'
    },
    experience: {
      title: '职业经验'
    },
    projects: {
      title: '开源项目',
      viewOn: '查看于'
    },
    skills: {
      title: '技能',
      certifications: '认证',
      categories: {
        cloud: '云与基础设施',
        data: '数据与AI',
        devops: 'DevOps与监控',
        programming: '编程',
        management: '管理'
      }
    },
    blog: {
      title: '博客',
      readMore: '阅读更多',
      backToBlog: '返回博客',
      loading: '加载中...',
      empty: '暂无文章。请稍后再来！',
      notFound: '文章未找到'
    },
    cv: {
      title: '简历',
      download: '下载简历',
      loading: '加载中...'
    },
    contact: {
      title: '联系',
      description: '对合作或项目感兴趣吗？欢迎与我联系。'
    },
    footer: {
      rights: '版权所有。',
      madeWith: '使用SolidJS和Bun构建'
    }
  }
};
